import React, { useEffect, useState } from 'react';

// Subtle, dismissible "Install app" pill — phone only.
//
// Two paths, because the browsers differ:
//   • Android / Chromium: we catch the `beforeinstallprompt` signal and
//     install on tap (one touch, no menu digging).
//   • iOS / Safari: no such signal exists, so tapping reveals the
//     "Share → Add to Home Screen" instructions instead.
//
// It never shows on desktop, never shows once the app is already installed
// (running standalone), and stays gone for good once dismissed.

const DISMISS_KEY = 'vegetablah_install_dismissed';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const detectPhone = () => {
  const ua = window.navigator.userAgent || '';
  return /android|iphone|ipod|ipad|mobile/i.test(ua);
};

const detectIOS = () => {
  const ua = window.navigator.userAgent || '';
  return (
    /iphone|ipod|ipad/i.test(ua) ||
    // iPadOS 13+ reports as MacIntel but is touch-capable
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  const isIOS = detectIOS();

  useEffect(() => {
    // Bail out entirely for anything that shouldn't see this.
    if (isStandalone() || !detectPhone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    // Android / Chromium: wait for the browser to say it's installable.
    const onBeforeInstall = (e) => {
      e.preventDefault(); // stop Chrome's own mini-infobar
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Once installed, disappear and never nag again.
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, '1');
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS never fires beforeinstallprompt — surface the pill directly so
    // the Share-sheet instructions are reachable.
    if (isIOS) setVisible(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [isIOS]);

  const dismiss = () => {
    setVisible(false);
    setShowIOSHelp(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSHelp((v) => !v);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      // The prompt can only be used once.
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pointer-events-none"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
    >
      <div className="pointer-events-auto w-full max-w-sm rounded-2xl bg-surface-card dark:bg-surface-dark-card border border-surface-border dark:border-surface-dark-border shadow-lg overflow-hidden">
        {showIOSHelp && (
          <div className="px-4 pt-3 pb-2 text-sm text-gray-600 dark:text-gray-300 border-b border-surface-border dark:border-surface-dark-border">
            In <span className="font-semibold">Safari</span>, tap the{' '}
            <span className="font-semibold">Share</span> icon{' '}
            <span aria-hidden="true">&#x2191;&#xFE0E;</span>, then{' '}
            <span className="font-semibold">&ldquo;Add to Home Screen.&rdquo;</span>
          </div>
        )}
        <div className="flex items-center gap-3 px-3 py-2.5">
          <span className="text-xl leading-none" aria-hidden="true">📲</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
              Install Vegetablah
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight truncate">
              Add it to your home screen
            </p>
          </div>
          <button
            onClick={handleInstall}
            className="shrink-0 rounded-lg bg-primary-500 hover:bg-primary-400 text-[#1a1200] shadow-glow-sm text-sm font-semibold px-3.5 py-1.5 transition-colors"
          >
            {isIOS ? 'How' : 'Install'}
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
